import { getStore } from 'app/state/storeRegistry'
import {
  KeplerLayer,
  KeplerTable,
  PopupSettings,
  SpatialDataType,
  WidgetType,
  TimeSeriesWidget,
  Widget
} from '../types'
import { getLayerById, getkeplerDatasetById } from '../state/keplerGlSlice'
import { runAppSaga } from 'app/state/store'
import { refreshDataset } from '../state/datasetSagas'
import { getLayerTypeChangeIncompatibleSettings, isUnknownSpatialSettingsDataset } from '../state/builderSliceUtils'
import { replaceWidget, setLayerPopupSettings } from '../state/builderSlice'
import { patchDataset } from 'features/maps/api/maps'
import { updateCurrentMapDataset } from 'features/maps/state/mapsSlice'
import { formatGeoColumn, getSourceSpatialDataType, parseGeoColumn } from '../utils/geoColumn'
import { SchemaField } from 'features/data-explorer/types'
import { askQuestionWithDialog } from 'features/dialogs/askQuestionWithDialog'
import { removeLayer } from '@carto/kepler.gl/actions'
import { RequestStatus } from 'features/common/types'

const LayerTypeChangeConfirmDialogLazy = () =>
  import('../ui/KeplerGl/SidePanel/LayerPanel/LayerTypeChangeConfirmDialog')

export type UpdateLayerSpatialDataSettingsProps = {
  layerId: string
  spatialDataColumn: string
  spatialDataType: SpatialDataType
}

export async function updateLayerSpatialDataSettings({
  layerId,
  spatialDataColumn,
  spatialDataType
}: UpdateLayerSpatialDataSettingsProps): Promise<void> {
  const store = getStore()
  const mapId = store.getState().maps.currentMap.id
  const layer = getLayerById(store.getState(), layerId) as KeplerLayer
  const datasetId = layer.config.dataId
  try {
    const keplerDataset: KeplerTable | undefined = getkeplerDatasetById(store.getState(), datasetId)
    const oldGeoColumn = keplerDataset?.metadata?.geoColumn
    const schema: SchemaField[] | undefined = keplerDataset?.metadata?.schema

    if (!layer || !keplerDataset || !schema) {
      throw new Error('Internal error: Invalid internal state')
    }

    let newGeoColumn: string
    const oldSpatialDataType = oldGeoColumn && getSourceSpatialDataType(oldGeoColumn, schema)
    if (oldSpatialDataType === spatialDataType) {
      // we keep same spatial data type, in geoColumn regardless of spatialDataType

      // oldGeoColumn quadbin:geom
      //   oldSettings geom/geom
      //   newSettings geom/geom3
      //
      // expected new geoColumn quadbin:geom3
      const parsedOldGeoColumn = parseGeoColumn(oldGeoColumn)
      newGeoColumn = formatGeoColumn({
        spatialDataColumn,
        spatialDataType: parsedOldGeoColumn?.spatialDataType || spatialDataType
      })
    } else {
      // for other cases, we update spatial data type in geoColumn to one used by user
      newGeoColumn = formatGeoColumn({ spatialDataColumn, spatialDataType })
    }

    if (!(await layerTypeChangesIncompatibleUserPrompt({ layerId, changeType: 'spatialDefinitionChange' }))) {
      return
    }

    store.dispatch(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: {
          status: RequestStatus.Pending
        }
      })
    )

    await removeIncompatibleMapFeatures({ layer })

    await patchDataset({ mapId, datasetId, body: { geoColumn: newGeoColumn } })
    store.dispatch(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: {
          geoColumn: newGeoColumn
        }
      })
    )

    await runAppSaga(function* () {
      // refreshDataset should change current layer to new layer type if needed
      yield refreshDataset({ datasetId, propsToUpdate: { geoColumn: newGeoColumn }, noCache: false })
    })

    const datasetAfterUpdate = getkeplerDatasetById(store.getState(), datasetId)
    if (!datasetAfterUpdate || isUnknownSpatialSettingsDataset(datasetAfterUpdate) || datasetAfterUpdate.error) {
      if (datasetAfterUpdate.error) {
        throw new Error(`Dataset update failed: ${datasetAfterUpdate.error}`)
      } else {
        throw new Error('Dataset update failed: Unable to apply spatial data settings')
      }
    }
  } catch (error) {
    store.dispatch(
      updateCurrentMapDataset({
        id: datasetId,
        dataset: {
          status: RequestStatus.Rejected,
          errorMessage: (error as Error).message,
          runningTo: Date.now()
        }
      })
    )
    throw new Error(`Failed to update spatial data settings: ${error}`, { cause: error })
  }
}

export async function layerTypeChangesIncompatibleUserPrompt({
  layerId,
  changeType
}: {
  layerId: string
  changeType: string
}) {
  const store = getStore()
  const state = store.getState()
  const { layers, popupSettings, widgets } = {
    layers: state.keplerGl.map?.visState?.layers as KeplerLayer[],
    popupSettings: state.builder.popupSettings as PopupSettings,
    widgets: state.builder.widgets as Widget[]
  }

  const layerTypeChangeIncompatible: string[] = getLayerTypeChangeIncompatibleSettings(
    layers,
    popupSettings,
    widgets,
    layerId
  )

  if (layerTypeChangeIncompatible.length > 0) {
    const response = await askQuestionWithDialog(LayerTypeChangeConfirmDialogLazy, {
      changeType,
      elements: layerTypeChangeIncompatible
    })
    if (response !== 'continue') return false
  }
  return true
}

export async function removeIncompatibleMapFeatures({ layer }: { layer: KeplerLayer }) {
  const store = getStore()

  const layerId = layer.id
  const datasetId = layer.config.dataId

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const removedLayerIdx = store
      .getState()
      .keplerGl.map.visState.layers.findIndex(
        (otherLayer) => otherLayer !== layer && otherLayer.config.dataId === datasetId
      )

    if (removedLayerIdx === -1) {
      break
    }
    store.dispatch(removeLayer(removedLayerIdx))
  }

  store.dispatch(setLayerPopupSettings({ layerId, settings: { enabled: false } }))

  const widgets: Widget[] = store.getState().builder.widgets
  const timeSeriesWidgets = widgets.filter(
    (widget) => widget.type === WidgetType.TIMESERIES && widget.dataSource === datasetId
  ) as TimeSeriesWidget[]

  for (const widget of timeSeriesWidgets) {
    store.dispatch(replaceWidget({ ...widget, showControls: false }))
  }
}
